// Copyright (C) 2011 by Will Tomlins
//
// Github profile: http://github.com/layam
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { IntlShape } from '@alltrails/shared/react-intl';
import LanguageRegionCode from '@alltrails/shared/types/LanguageRegionCode';
import { MILLISECONDS_IN_YEAR } from 'constants/DateConstants';
import '@formatjs/intl-relativetimeformat/polyfill';

enum DateFormatKey {
  Second = 'second',
  Minute = 'minute',
  Hour = 'hour',
  Day = 'day',
  Month = 'month',
  Year = 'year'
}

const timeUnits: [number, DateFormatKey][] = [
  // Time units must be be ordered largest -> smallest
  [31556926, DateFormatKey.Year],
  [2629744, DateFormatKey.Month],
  [86400, DateFormatKey.Day],
  [3600, DateFormatKey.Hour],
  [60, DateFormatKey.Minute],
  [1, DateFormatKey.Second]
];

function getTimeBreakdown(seconds_difference: number, isPast: boolean): { unit: DateFormatKey; difference: number } {
  let seconds = Math.abs(seconds_difference);
  const breakdown = {} as Record<DateFormatKey, number>;

  for (let i = 0; i < timeUnits.length; i++) {
    const occurences_of_unit = Math.floor(seconds / timeUnits[i][0]);
    seconds -= timeUnits[i][0] * occurences_of_unit;
    breakdown[timeUnits[i][1]] = occurences_of_unit;
  }

  const unit = Object.keys(breakdown).find((key: DateFormatKey) => breakdown[key] > 0) as DateFormatKey;
  let difference = breakdown[unit];

  if (isPast) {
    difference *= -1;
  }

  return { unit, difference };
}

function humanizeTimeSpan(timestamp: string, intl: IntlShape, languageRegionCode?: LanguageRegionCode) {
  const date = new Date(timestamp);
  const locale = languageRegionCode || 'en-US';

  // if date is more than a year ago, format it as (Month, year)
  const now = new Date(Date.now());
  const diff = +now - +date;
  if (diff > MILLISECONDS_IN_YEAR) {
    return date.toLocaleDateString(locale, { year: 'numeric', month: 'long' });
  }

  let seconds_difference = (date.getTime() - now.getTime()) / 1000;

  const { unit, difference } = getTimeBreakdown(seconds_difference, seconds_difference < 0);
  return intl.formatRelativeTime(difference, unit);
}

export default humanizeTimeSpan;
